import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  forensicRecoveryStatusSchema,
  type ForensicRecoveryPath,
  type ForensicRecoveryStatus,
} from "@shared/forensic-recovery-status";
import {
  cancelForensicRecovery,
  collectForensicRecoveryPasskey,
  finishForensicRecovery,
  returnToSignIn,
  retryForensicRecoveryGuardianCeremony,
  startForensicRecoveryPath,
  submitForensicRecoveryCode,
  vetoForensicRecoveryWithCredential,
} from "@/lib/forensic-recovery-api";
import type { WardLiftChallenge } from "@/lib/forensic-recovery-lift-challenge";
import { apiRequest, ApiError } from "@/lib/queryClient";

export const FORENSIC_RECOVERY_STATUS_KEY = ["security", "forensic-recovery", "status"] as const;

export interface UseForensicRecoveryChooserResult {
  readonly recoveryStatus: ForensicRecoveryStatus | undefined;
  readonly isRecoveryBusy: boolean;
  readonly recoveryError: string | null;
  readonly startRecovery: (path: ForensicRecoveryPath, challenge?: WardLiftChallenge) => Promise<void>;
  readonly continueRecovery: (attemptId: string, challenge?: WardLiftChallenge) => Promise<void>;
  readonly submitRecoveryCode: (attemptId: string, code: string) => Promise<void>;
  readonly cancelRecovery: (attemptId: string) => Promise<void>;
  readonly vetoRecovery: (attemptId: string) => Promise<void>;
}

async function fetchRecoveryStatus(): Promise<ForensicRecoveryStatus> {
  const response = await apiRequest("/api/security/forensic-recovery/status");
  return forensicRecoveryStatusSchema.parse(response);
}

function isDefinitiveLiftRejection(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 400 || error.status === 409);
}

/**
 * Read-only view of the ONE server-owned pause fact (AC-53). It shares
 * `FORENSIC_RECOVERY_STATUS_KEY` with the chooser the app shell mounts, so a
 * surface that suppresses itself "because the shell banner has it" is reading
 * the SAME cache entry the shell renders from — never a second, client-derived
 * approximation that can disagree and leave the incident with no surface at
 * all. `undefined` (unloaded/failed) is not a pause: fail towards showing.
 */
export function useForensicPauseActive(): boolean {
  return usePauseStatusQuery().data?.pause === "active";
}

/**
 * The FAIL-CLOSED counterpart: true only once the status read has resolved
 * successfully AND says the pause is inactive. Loading and errored (429/503…)
 * are both false. For surfaces that must not invite a mutation during a pause
 * they cannot rule out (the day-2 confirm card: its reconfirm POST would 423
 * after the person's UV). Same cache entry as `useForensicPauseActive`. Pass `{ enabled: false }` when the
 * calling surface is switched off, so it adds no status fetches.
 */
export function useForensicPauseResolvedInactive(options: { enabled?: boolean } = {}): boolean {
  const { data, isSuccess } = usePauseStatusQuery(options.enabled ?? true);
  return isSuccess && data?.pause === "inactive";
}

/** `enabled: false` stops THIS observer fetching (the status GET is rate-limited
 *  per user and the shell polls it during a pause); a cached entry still reads. */
function usePauseStatusQuery(enabled = true) {
  return useQuery({
    queryKey: FORENSIC_RECOVERY_STATUS_KEY,
    queryFn: fetchRecoveryStatus,
    staleTime: 15_000,
    retry: false,
    enabled,
  });
}

/** Owns semantic chooser status while keeping assertion/code material out of query state. */
export function useForensicRecoveryChooser(): UseForensicRecoveryChooserResult {
  const queryClient = useQueryClient();
  const busyRef = useRef(false);
  const [isRecoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const statusQuery = useQuery({
    queryKey: FORENSIC_RECOVERY_STATUS_KEY,
    queryFn: fetchRecoveryStatus,
    staleTime: 15_000,
    retry: false,
    refetchInterval: (query) => query.state.data?.pause === "active" ? 15_000 : false,
  });

  useEffect(() => {
    if (!(statusQuery.error instanceof ApiError) || statusQuery.error.status !== 401) return;
    queryClient.clear();
    window.location.href = "/auth";
  }, [queryClient, statusQuery.error]);

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: FORENSIC_RECOVERY_STATUS_KEY });
  }, [queryClient]);

  const run = useCallback(async (work: () => Promise<void>): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;
    setRecoveryError(null);
    setRecoveryBusy(true);
    try {
      await work();
    } catch {
      setRecoveryError("Recovery could not continue. Try again.");
    } finally {
      try { await refresh(); } catch { /* retain the generic operation outcome */ }
      setRecoveryBusy(false);
      busyRef.current = false;
    }
  }, [refresh]);

  const startRecovery = useCallback(async (path: ForensicRecoveryPath, challenge?: WardLiftChallenge): Promise<void> => {
    await run(async () => {
      if (challenge) await startForensicRecoveryPath(path, challenge);
      else await startForensicRecoveryPath(path);
    });
  }, [run]);

  const continueRecovery = useCallback(async (attemptId: string, challenge?: WardLiftChallenge): Promise<void> => {
    const attempt = statusQuery.data?.attempt;
    if (!attempt || attempt.attemptId !== attemptId) return;
    if (attempt.nextAction === "lift") {
      if (busyRef.current) return;
      busyRef.current = true;
      setRecoveryError(null);
      setRecoveryBusy(true);
      const leaveRecovery = (): void => {
        queryClient.clear();
        returnToSignIn();
      };
      try {
        await finishForensicRecovery(attemptId);
        leaveRecovery();
      } catch (error) {
        if (isDefinitiveLiftRejection(error)) {
          try {
            const current = await fetchRecoveryStatus();
            if (current.pause === "inactive") leaveRecovery();
            else {
              queryClient.setQueryData(FORENSIC_RECOVERY_STATUS_KEY, current);
              setRecoveryError("Recovery could not continue. Try again.");
            }
          } catch {
            leaveRecovery();
          }
        } else {
          if (!(error instanceof ApiError && error.status === 401)) {
            try { await fetchRecoveryStatus(); } catch { /* interruption remains ambiguous */ }
          }
          leaveRecovery();
        }
      } finally {
        setRecoveryBusy(false);
        busyRef.current = false;
      }
      return;
    }
    await run(async () => {
      if (attempt.nextAction === "passkey_1" || attempt.nextAction === "passkey_2") {
        await collectForensicRecoveryPasskey(attemptId,
          attempt.nextAction === "passkey_2" ? "2" : "1");
      } else if (attempt.nextAction === "retry_guardian") {
        if (challenge) await retryForensicRecoveryGuardianCeremony(attemptId, challenge);
        else await retryForensicRecoveryGuardianCeremony(attemptId);
      } else {
        throw new Error("RECOVERY_WAITING");
      }
    });
  }, [queryClient, run, statusQuery.data?.attempt]);

  const submitRecoveryCode = useCallback(async (attemptId: string, code: string): Promise<void> => {
    await run(async () => { await submitForensicRecoveryCode(attemptId, code); });
  }, [run]);

  const cancelRecovery = useCallback(async (attemptId: string): Promise<void> => {
    await run(async () => { await cancelForensicRecovery(attemptId); });
  }, [run]);

  const vetoRecovery = useCallback(async (attemptId: string): Promise<void> => {
    await run(async () => { await vetoForensicRecoveryWithCredential(attemptId); });
  }, [run]);

  return {
    recoveryStatus: statusQuery.data,
    isRecoveryBusy,
    recoveryError,
    startRecovery,
    continueRecovery,
    submitRecoveryCode,
    cancelRecovery,
    vetoRecovery,
  };
}
